import { Suspense, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Tabs } from "flowbite-react";

import {
  TableFilters,
  TableFiltersState,
} from "@/common/components/ui/table/TableFilters";

import Loading from "@/common/components/ui/loading/Loading";
import GenericTable from "@/common/components/ui/table/GenericTable";
import TableFooter from "@/common/components/ui/table/TableFooter";

import {
  getFinanceExclusionHistory,
  FinanceExclusionHistory,
  ExclusionEntityType,
} from "@/common/services/finance-exclusion-history.api";
import { IRenter } from "@/types/renter.type";
import { useCompanyStore } from "@/common/states/store/Company";
import { formatDate } from "@/common/helpers/date.helpers";

const headers = {
  renterName: "Cliente",
  paymentId: "Registro",
  authorName: "Autor",
  createdAt: "Criado em"
};

export default function TableExclusionHistory() {
  const navigate = useNavigate();
  const { selectedCompanies, selectedRegion, selectedUf, filterMode } =
    useCompanyStore();


  const [activeTab, setActiveTab] = useState<ExclusionEntityType>("PAYMENT");

  const [rows, setRows] = useState<FinanceExclusionHistory[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const [page, setPage] = useState<number>(1);
  const [data, setData] = useState({ itemsPerPage: 10, totalItems: 0 });

  const [filters, setFilters] = useState<TableFiltersState>({
    search: "",
    status: [],
    orderBy: "",
  });

  async function fetchHistory(currentFilters: TableFiltersState, currentPage: number) {
    setIsLoading(true);
    setRows([]);

    const result = await getFinanceExclusionHistory({
      entityType: activeTab,
      search: currentFilters.search || undefined,
      limit: data.itemsPerPage,
      page: currentPage,
    });

    setIsLoading(false);

    if (!result.ok) {
      console.error(
        (result as { message?: string })?.message ||
        "Erro ao buscar histórico de exclusões."
      );
      setRows([]);
      setData((prev) => ({ ...prev, totalItems: 0 }));
      return;
    }

    const list = result.data ?? [];
    setRows(list);
    setData({
      itemsPerPage: data.itemsPerPage,
      totalItems: result.totalItems,
    });
  }

  useEffect(() => {
    fetchHistory(filters, page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeTab,
    filters.search,
    page,
    filterMode,
    selectedCompanies,
    selectedRegion,
    selectedUf,
  ]);


  const handleFilterChange = (newFilters: Partial<TableFiltersState>) => {
    setPage(1);
    setFilters((prev) => ({
      ...prev,
      ...newFilters,
    }));
  };

  const paged = useMemo(() => {
    return rows;
  }, [rows]);

  return (
    <Suspense fallback={<Loading />}>
      <div className="table-section">
        <TableFilters
          dashboardHeader="Histórico de Exclusões"
          placeholder="Buscar por cliente, registro ou autor..."
          newItemLabel=""
          onNewItemClick={() => { }}
          statusOptions={{}}
          onSearch={handleFilterChange}
          entity="finance-exclusion-history"
          tableData={rows as unknown as Record<string, unknown>[]}
          hideStatusFilter
        />

        <Tabs
          aria-label="Exclusion history tabs"
          onActiveTabChange={(i) => setActiveTab(i === 0 ? "PAYMENT" : "SUBSCRIPTION")}
        >
          <Tabs.Item active title="Pagamentos" />
          <Tabs.Item title="Assinaturas" />
        </Tabs>

        <GenericTable
          headers={headers}
          data={paged}
          setFilters={({ orderBy }) => handleFilterChange({ orderBy })}
          renders={{
            renterName: (name, row) => {
              const renterId = (row as IRenter)._id;

              return (
                <span
                  className={`font-medium text-gray-900 ${renterId
                    ? "hover:text-bluePrimary cursor-pointer transition-colors"
                    : ""
                    }`}
                  onClick={() => {
                    if (renterId) {
                      navigate(`/clientes/${renterId}`);
                    }
                  }}
                >
                  {String(name ?? "-")}
                </span>
              );
            },


            paymentId: (_paymentId, row) => {
              //eslint-disable-next-line @typescript-eslint/no-explicit-any
              const r = row as any;
              const id = r.id;

              const label = r.entityType === "SUBSCRIPTION" ? "Assinatura" : "Pagamento";

              return (
                <a
                  href={`/financeiro/historico-exclusoes/${r.entityType}/${id}`}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(`/financeiro/historico-exclusoes/${r.entityType}/${id}`);
                  }}
                  className="text-blue-600 hover:text-bluePrimary cursor-pointer font-medium"
                >
                  {label}
                </a>
              );
            },

            authorName: (_author, row) =>
              //eslint-disable-next-line @typescript-eslint/no-explicit-any
              String((row as any).authorName ?? (row as any).deletedByName ?? "-"),
            createdAt: formatDate,

          }}
          entity="finance-exclusion-history"
        />

        {isLoading && <Loading />}

        <TableFooter
          page={page || 1}
          totalItems={data.totalItems || 0}
          itemsPerPage={+(filters.itemsPerPage ?? 10)}
          goToPage={setPage}
        />
      </div>
    </Suspense>
  );
}
